import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { CSSTransition } from 'react-transition-group';
import { Link, Paper, Typography } from '@mui/material';

import { CLOSED } from '../../../shared/claimStatus';
import useInsuranceInfo from '../../../hooks/useInsuranceInfo';
import {
  claimStatus,
  CLEAR_CLAIM_NOTIFICATION,
  formatDate,
  GET_CLAIM,
  GET_CLAIMS,
  GET_CUSTOMER,
  INSURANCE_CATEGORY_PET_HEALTH,
  INSURANCE_CATEGORY_PET_LIABILITY,
  INSURANCE_TYPE_PETOLO,
  PETOLO_REIMBURSEMENT_HELPCENTER_LINK,
  useClaimStatus,
  useReady,
} from '../../../shared';

import { BackButton } from '../../../components/common';
import ErrorPlaceholder from '../../errorPlaceholder';
import { Loading } from '../../../components/ui';

import AlreadyUploadedDocuments from '../AlreadyUploadedDocuments';
import ClaimDetailsInfo from '../ClaimDetailsInfo';
import ClaimDetailsAdditionalPayments from '../ClaimDetailsAdditionalPayments';
import ClaimContactPerson from '../ClaimContactPerson';
import ClaimDetailsStatusHistory from '../ClaimDetailsStatusHistory';

import { StyledClaimStatus } from '../../ClaimsList/ClaimStatus.styled';
import { StyledVerticalContent, StyledHelpCenterTipContainer } from './UserClaimDetails.styled';

const renderHelpCenterLink = (chunk) => (
  <Link
    variant="p"
    href={PETOLO_REIMBURSEMENT_HELPCENTER_LINK}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunk}
  </Link>
);

const UserClaimDetails = ({ selectedClaim, enterdElement }) => {
  const ready = useReady();
  const claimStatusBar = useRef(null);
  const [claim, setClaim] = useState(null);
  const [latestPayout, setLatestPayout] = useState(null);
  const [otherPayouts, setOtherPayouts] = useState([]);
  const [claimLabel] = useClaimStatus(claim?.status);
  const { id } = useParams();
  const location = useLocation();
  const { category: insuranceCategory, type: insuranceType } = useInsuranceInfo();

  const { data: customerData, loading: customerLoading } = useQuery(GET_CUSTOMER);

  const { data: claimData, loading: claimLoading } = useQuery(GET_CLAIM, {
    variables: { id: Number(id) },
  });
  const isLoaderShown =
    enterdElement != null && enterdElement?.contains(claimStatusBar?.current) && !claim;
  const [clearClaimNotification] = useMutation(CLEAR_CLAIM_NOTIFICATION, {
    variables: {
      claimId: Number(id),
    },
    refetchQueries: [{ query: GET_CLAIMS }],
  });

  const isPetolo = insuranceType === INSURANCE_TYPE_PETOLO;

  useEffect(() => {
    if (!claimLoading) {
      setClaim(claimData?.claim);
      const claimPayouts = claimData?.claim?.payouts;
      if (claimPayouts?.length) {
        // payouts not yet been confirmed on claims with status auszahlung or ausgezahlt are displayed in the status_detail
        const sortedPayouts = Array.from(claimPayouts).sort(
          (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt)
        );
        setLatestPayout(sortedPayouts[0]);
        setOtherPayouts([...sortedPayouts.slice(1)]);
      }
    }
  }, [claimData, claimLoading]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
    if (claim && claim?.statusUpdated) {
      clearClaimNotification();
    }
  }, [location, clearClaimNotification, claim]);

  const skipHistoryItem = ['cancelled'];

  const showAdditionalPayouts = (status) =>
    status === claimStatus.CLOSED || status === claimStatus.WAITING_FOR_PAYOUT;

  const getClaimDate = () => {
    let date = '';
    if (selectedClaim) {
      date = selectedClaim?.lossDate || selectedClaim?.notificationDate;
    }
    if (claim) {
      date = claim?.lossDate || claim?.notificationDate;
    }
    return formatDate(date);
  };

  const treatmentCategory = selectedClaim
    ? selectedClaim?.treatmentCategory
    : claim?.treatmentCategory;

  if (!customerLoading && !!customerData && !!Object.keys(customerData).length) {
    return (
      <CSSTransition
        in={ready && !!(selectedClaim || claim)}
        timeout={450}
        classNames="fade"
        unmountOnExit
      >
        <>
          <BackButton to="/user-claims-info" />

          <StyledVerticalContent>
            <Paper>
              <StyledVerticalContent>
                {!!treatmentCategory && (
                  <Typography variant="h3">
                    <FormattedMessage id={`treatment.category.${treatmentCategory}`} />
                  </Typography>
                )}
                {!treatmentCategory && isPetolo && (
                  <Typography variant="h3">
                    <FormattedMessage id="treatment.category.pet_null" />
                  </Typography>
                )}

                <Typography variant="p" color="professional_light_blue" data-hj-suppress="true">
                  {getClaimDate()} ({selectedClaim ? selectedClaim?.key : claim?.key})
                </Typography>

                <StyledClaimStatus
                  claimStatus={selectedClaim ? selectedClaim?.claimLabel : claimLabel}
                >
                  <Typography variant="p">
                    <FormattedMessage
                      id={`claim.status.label.${selectedClaim ? selectedClaim?.status : claim?.status}`}
                      tagName="span"
                    />
                  </Typography>
                </StyledClaimStatus>

                {(insuranceCategory === INSURANCE_CATEGORY_PET_LIABILITY ||
                  insuranceCategory === INSURANCE_CATEGORY_PET_HEALTH) &&
                  (selectedClaim?.status === CLOSED || claim?.status === CLOSED) && (
                    <StyledHelpCenterTipContainer>
                      <Typography variant="p">
                        <FormattedMessage
                          id="claim.status.details.refund_amount.help_center_info"
                          values={{
                            a: (chunk) => renderHelpCenterLink(chunk),
                          }}
                        />
                      </Typography>
                    </StyledHelpCenterTipContainer>
                  )}
              </StyledVerticalContent>
            </Paper>

            {insuranceCategory !== INSURANCE_CATEGORY_PET_LIABILITY && (
              <ClaimDetailsInfo
                claimLabel={claimLabel}
                selectedClaim={selectedClaim}
                claim={claim}
                latestPayout={latestPayout}
                customer={customerData?.customer}
              />
            )}

            {Array.isArray(claimData?.claim?.documentsAttachments) &&
              claimData.claim.documentsAttachments.length > 0 && (
                <AlreadyUploadedDocuments
                  documentsAttachments={claimData?.claim?.documentsAttachments}
                />
              )}

            {otherPayouts?.length > 0 && showAdditionalPayouts(claim?.status) && (
              <ClaimDetailsAdditionalPayments
                otherPayouts={otherPayouts}
                customerIBAN={customerData?.customer?.iban}
              />
            )}

            {claim?.externalContact?.externalClaimId && <ClaimContactPerson claim={claim} />}

            {insuranceCategory !== INSURANCE_CATEGORY_PET_LIABILITY && (
              <ClaimDetailsStatusHistory claim={claim} skipHistoryItem={skipHistoryItem} />
            )}
          </StyledVerticalContent>

          {isLoaderShown && <Loading showLogo />}
        </>
      </CSSTransition>
    );
  }

  if (!customerLoading) {
    return (
      <CSSTransition in={ready} timeout={600} classNames="slow-fade" unmountOnExit>
        <ErrorPlaceholder />
      </CSSTransition>
    );
  }

  return null;
};

export default UserClaimDetails;
